// @ts-check
/* CUSTOM: "Skrab-popup" til e-mail-tilmelding (17/10 2026, som Cascalias Alia-popup) – se snippets/custom-scratch-popup.

   Snippet'et renderes kun, når popuppen er slået til, siden er en kollektion/et produkt og kunden ikke er logget ind
   (layout/theme.liquid). Her afgøres resten:
   - vis IKKE hvis: tilmeldt via popuppen (permanent), lukket inden for "days" dage, Klaviyo kender personen
     (cookie __kla_id med $exchange_id), eller man kommer fra en Klaviyo-/nyhedsbrevs-link (utm / _kx).
   - temaeditoren (Shopify.designMode): vis med det samme og ignorer alle spærrer.
   - timeren starter først, når cookie-banneret (Pandectes via Shopifys Customer Privacy API) er væk.
   - trigger: "delay" sekunder ELLER scrollet "scroll" % – det der kommer først. Kun én gang pr. sideindlæsning.

   Ren logik (uden DOM) ligger i window.CustomScratchPopup – kan testes direkte i konsollen:
   CustomScratchPopup.shouldShow({ state: { dismissedAt: Date.now() }, now: Date.now(), days: 14 })
*/

/** @typedef {{ subscribed?: boolean, dismissedAt?: number }} PopupState */
/** @typedef {{ data: ArrayLike<number>, width: number, height: number }} PixelData */

/** @type {any} */ (window).CustomScratchPopup = (() => {
  const DAY_MS = 24 * 60 * 60 * 1000;
  const MARKETING_SOURCES = ['klaviyo', 'email', 'sms', 'newsletter', 'nyhedsbrev'];

  /**
   * Skal popuppen vises på denne sideindlæsning?
   * @param {{ state?: PopupState | null, now: number, days: number, klaviyoIdentified?: boolean, utm?: boolean, designMode?: boolean }} input
   * @returns {{ show: boolean, reason: string }}
   */
  function shouldShow({ state, now, days, klaviyoIdentified = false, utm = false, designMode = false }) {
    if (designMode) return { show: true, reason: 'design-mode' };
    if (state && state.subscribed) return { show: false, reason: 'subscribed' };
    if (state && typeof state.dismissedAt === 'number' && now - state.dismissedAt < days * DAY_MS) {
      return { show: false, reason: 'dismissed' };
    }
    if (klaviyoIdentified) return { show: false, reason: 'klaviyo-identified' };
    if (utm) return { show: false, reason: 'marketing-link' };
    return { show: true, reason: 'new-visitor' };
  }

  /**
   * Kender Klaviyo personen? Cookien __kla_id er base64-kodet JSON; en identificeret profil har $exchange_id.
   * @param {string | null | undefined} cookieValue
   * @returns {boolean}
   */
  function isKlaviyoIdentified(cookieValue) {
    if (!cookieValue) return false;
    try {
      let b64 = decodeURIComponent(cookieValue).replace(/-/g, '+').replace(/_/g, '/');
      while (b64.length % 4) b64 += '=';
      const data = JSON.parse(atob(b64));
      return Boolean(data && data.$exchange_id);
    } catch (e) {
      return false;
    }
  }

  /**
   * Kommer man fra en mail/sms/nyhedsbrev (utm_source/utm_medium) eller et Klaviyo-link (_kx)?
   * @param {string} searchString fx location.search
   * @returns {boolean}
   */
  function hasMarketingUtm(searchString) {
    let params;
    try {
      params = new URLSearchParams(searchString || '');
    } catch (e) {
      return false;
    }
    if (params.has('_kx')) return true;
    return ['utm_source', 'utm_medium'].some((key) => {
      const value = (params.get(key) || '').trim().toLowerCase();
      return MARKETING_SOURCES.includes(value);
    });
  }

  /**
   * Andel af skrabe-fladen der er skrabet væk (0–1). Sampler hver 4. pixel; alpha < 128 = skrabet.
   * @param {PixelData | null | undefined} imageData
   * @returns {number}
   */
  function coverage(imageData) {
    if (!imageData || !imageData.data || !imageData.width || !imageData.height) return 0;
    const data = imageData.data;
    const pixels = Math.min(imageData.width * imageData.height, Math.floor(data.length / 4));
    let sampled = 0;
    let cleared = 0;
    for (let p = 0; p < pixels; p += 4) {
      sampled += 1;
      if (data[p * 4 + 3] < 128) cleared += 1;
    }
    return sampled ? cleared / sampled : 0;
  }

  /**
   * Simpel e-mail-kontrol (Klaviyo validerer igen på serveren).
   * @param {string} str
   * @returns {boolean}
   */
  function isValidEmail(str) {
    const value = String(str || '').trim();
    return value.length <= 254 && /^[^\s@]+@[^\s@.]+(\.[^\s@.]+)*\.[^\s@.]{2,}$/.test(value);
  }

  return { shouldShow, isKlaviyoIdentified, hasMarketingUtm, coverage, isValidEmail };
})();

(() => {
  const dialog = /** @type {HTMLDialogElement | null} */ (document.querySelector('[data-custom-scratch-popup]'));
  const fab = /** @type {HTMLElement | null} */ (document.querySelector('[data-custom-scratch-fab]'));
  const configEl = document.querySelector('[data-custom-scratch-popup-config]');
  if (!dialog || !fab || !configEl || typeof dialog.showModal !== 'function') return;

  /** @type {any} */
  const win = window;
  const logic = win.CustomScratchPopup;

  /** @type {{ delay: number, scroll: number, days: number, listId: string, template: string, testMode?: boolean, coverText: string, coverImage?: string, winText: string, texts: Record<string, string> }} */
  let config;
  try {
    config = JSON.parse(configEl.textContent || '{}');
  } catch (e) {
    return;
  }

  const STORAGE_KEY = 'custom_scratch_popup';
  const SESSION_FAB_KEY = 'custom_scratch_popup_fab';
  const POPUP_NAME = 'skrab-popup';
  const COMPANY_ID = 'SYk57N';
  const KLAVIYO_URL = `https://a.klaviyo.com/client/subscriptions/?company_id=${COMPANY_ID}`;
  const REVEAL_AT = 0.4;
  const BRUSH = 20;
  const designMode = Boolean(win.Shopify && win.Shopify.designMode === true);
  // Testtilstand (Temaindstillinger): vis for alle, gem intet – timer/scroll og samtykke gælder stadig
  const testMode = Boolean(config.testMode);
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const desktop = window.matchMedia('(min-width: 1024px)');

  const card = /** @type {HTMLElement} */ (dialog.querySelector('[data-csp-card]'));
  const canvas = /** @type {HTMLCanvasElement} */ (dialog.querySelector('[data-csp-canvas]'));
  const live = /** @type {HTMLElement} */ (dialog.querySelector('[data-csp-live]'));
  const form = /** @type {HTMLFormElement} */ (dialog.querySelector('[data-csp-form]'));
  const emailInput = /** @type {HTMLInputElement} */ (form.querySelector('input[type="email"]'));
  const errorEl = /** @type {HTMLElement} */ (dialog.querySelector('[data-csp-error]'));
  const answerErrorEl = /** @type {HTMLElement} */ (dialog.querySelector('[data-csp-error-answer]'));
  const chipsEl = /** @type {HTMLElement} */ (dialog.querySelector('[data-csp-chips]'));
  const submitBtn = /** @type {HTMLButtonElement} */ (dialog.querySelector('[data-csp-submit]'));
  const submitLabel = submitBtn.textContent || '';

  /** @type {'scratch' | 'email' | 'success'} */
  let step = 'scratch';
  let revealed = false;
  let subscribed = false;
  let shown = false; // vist på denne sideindlæsning
  let fabDismissed = false;
  let triggered = false;

  /* ---------- storage (altid i try/catch: privat tilstand / blokeret lager) ---------- */

  /** @returns {PopupState} */
  function readState() {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}') || {};
    } catch (e) {
      return {};
    }
  }

  /** @param {PopupState} patch */
  function saveState(patch) {
    if (designMode || testMode) return;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...readState(), ...patch }));
    } catch (e) { /* ignorer */ }
  }

  /** @returns {{ step: string, revealed: boolean } | null} */
  function readSessionFab() {
    try {
      const raw = sessionStorage.getItem(SESSION_FAB_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  }

  function saveSessionFab() {
    if (designMode || testMode) return;
    try {
      sessionStorage.setItem(SESSION_FAB_KEY, JSON.stringify({ step, revealed }));
    } catch (e) { /* ignorer */ }
  }

  function clearSessionFab() {
    try {
      sessionStorage.removeItem(SESSION_FAB_KEY);
    } catch (e) { /* ignorer */ }
  }

  /** @param {string} name */
  function readCookie(name) {
    const match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
    return match ? match[1] : null;
  }

  /* ---------- tracking (aldrig e-mailadressen) ---------- */

  /**
   * @param {string} event
   * @param {Record<string, unknown>} [extra]
   */
  function track(event, extra) {
    win.dataLayer = win.dataLayer || [];
    win.dataLayer.push({ event, popup_name: POPUP_NAME, page_template: config.template, ...extra });
  }

  /* ---------- skrabekort (canvas) ---------- */

  /** @type {CanvasRenderingContext2D | null} */
  let ctx = null;
  /** @type {{ x: number, y: number } | null} */
  let last = null;
  let moves = 0;
  let drawing = false;
  let drawnFor = '';

  /** Fast frø → samme ridser og korn ved hver gentegning (fx ved resize) */
  function seededRandom(/** @type {number} */ seed) {
    let t = seed >>> 0;
    return () => {
      t = (t + 0x6d2b79f5) >>> 0;
      let r = Math.imul(t ^ (t >>> 15), 1 | t);
      r = (r + Math.imul(r ^ (r >>> 7), 61 | r)) ^ r;
      return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
    };
  }

  /** @type {HTMLCanvasElement | null} */
  let noiseTile = null;

  /** Lille støj-flise (folie-korn), laves én gang og gentages som mønster – billigt selv ved 3x */
  function getNoiseTile() {
    if (noiseTile) return noiseTile;
    const size = 96;
    const tile = document.createElement('canvas');
    tile.width = size;
    tile.height = size;
    const tctx = tile.getContext('2d');
    if (!tctx) return null;
    const img = tctx.createImageData(size, size);
    const rand = seededRandom(7);
    for (let i = 0; i < img.data.length; i += 4) {
      const v = rand();
      const light = v > 0.5;
      img.data[i] = light ? 255 : 70;
      img.data[i + 1] = light ? 248 : 52;
      img.data[i + 2] = light ? 220 : 16;
      img.data[i + 3] = Math.round(Math.abs(v - 0.5) * 2 * 70); // 0–70 af 255: svagt, men synligt som fint korn
    }
    tctx.putImageData(img, 0, 0);
    noiseTile = tile;
    return tile;
  }

  /**
   * Tekst centreret med manuel bogstavafstand (ctx.letterSpacing findes ikke i alle browsere og
   * giver en skæv midte, fordi luften også lægges efter sidste bogstav).
   * @param {CanvasRenderingContext2D} c @param {string} text @param {number} cx @param {number} cy @param {number} spacing
   */
  function fillSpacedText(c, text, cx, cy, spacing) {
    const chars = Array.from(text);
    const widths = chars.map((ch) => c.measureText(ch).width);
    const total = widths.reduce((a, b) => a + b, 0) + spacing * Math.max(0, chars.length - 1);
    let x = cx - total / 2;
    c.textAlign = 'left';
    chars.forEach((ch, i) => {
      c.fillText(ch, x, cy);
      x += widths[i] + spacing;
    });
  }

  /* Valgfrit folie-billede fra temaindstillingen (crossOrigin, så getImageData ikke bliver "tainted").
     Er det ikke hentet endnu, tegnes temaets egen folie – og kortet tegnes om, når billedet kommer (hvis urørt). */
  /** @type {HTMLImageElement | null} */
  let coverImg = null;
  if (config.coverImage) {
    coverImg = new Image();
    coverImg.crossOrigin = 'anonymous';
    coverImg.decoding = 'async';
    coverImg.addEventListener('load', () => {
      if (dialog.open && !revealed && moves === 0 && !drawing) {
        drawnFor = '';
        drawCover();
      }
    });
    coverImg.addEventListener('error', () => { coverImg = null; });
    coverImg.src = config.coverImage;
  }

  /** Tegner folie-billedet "cover"-tilpasset. @returns {boolean} true hvis tegnet */
  function drawCoverImage(/** @type {CanvasRenderingContext2D} */ c, /** @type {number} */ w, /** @type {number} */ h) {
    if (!coverImg || !coverImg.complete || !coverImg.naturalWidth) return false;
    const iw = coverImg.naturalWidth;
    const ih = coverImg.naturalHeight;
    const scale = Math.max(w / iw, h / ih);
    const dw = iw * scale;
    const dh = ih * scale;
    try {
      c.drawImage(coverImg, (w - dw) / 2, (h - dh) / 2, dw, dh);
      return true;
    } catch (e) {
      return false;
    }
  }

  function drawCover() {
    if (revealed) return;
    const rect = card.getBoundingClientRect();
    // Mål uden transform (kortet skaleres ved afsløring) – offsetWidth er layout-størrelsen
    const w = card.offsetWidth || rect.width;
    const h = card.offsetHeight || rect.height;
    if (!w || !h) return;
    const key = `${Math.round(w)}x${Math.round(h)}`;
    if (key === drawnFor) return;
    drawnFor = key;

    const dpr = Math.min(window.devicePixelRatio || 1, 3);
    canvas.width = Math.round(w * dpr);
    canvas.height = Math.round(h * dpr);
    ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return;
    const c = ctx;
    c.setTransform(dpr, 0, 0, dpr, 0, 0);
    c.globalCompositeOperation = 'source-over';
    c.globalAlpha = 1;
    c.shadowBlur = 0;
    c.shadowColor = 'transparent';

    if (drawCoverImage(c, w, h)) {
      // Ejerens eget folie-billede – kun teksten lægges ovenpå (sektion 5)
    } else {
    // 1) Guldfolie: flerstops-gradient i ca. 35°
    const angle = (35 * Math.PI) / 180;
    const len = Math.abs(w * Math.cos(angle)) + Math.abs(h * Math.sin(angle));
    const dx = (Math.cos(angle) * len) / 2;
    const dy = (Math.sin(angle) * len) / 2;
    const base = c.createLinearGradient(w / 2 - dx, h / 2 - dy, w / 2 + dx, h / 2 + dy);
    base.addColorStop(0, '#c9a24a');
    base.addColorStop(0.28, '#f3e1a0');
    base.addColorStop(0.5, '#d9b85c');
    base.addColorStop(0.74, '#b8912c');
    base.addColorStop(1, '#e9d48c');
    c.fillStyle = base;
    c.fillRect(0, 0, w, h);

    // 2) Blødt, lysere specular-bånd diagonalt (modsat retning af basen)
    const spec = c.createLinearGradient(0, h, w, 0);
    spec.addColorStop(0.3, 'rgba(255, 249, 225, 0)');
    spec.addColorStop(0.46, 'rgba(255, 249, 225, 0.42)');
    spec.addColorStop(0.52, 'rgba(255, 249, 225, 0.5)');
    spec.addColorStop(0.66, 'rgba(255, 249, 225, 0)');
    c.fillStyle = spec;
    c.fillRect(0, 0, w, h);

    const rand = seededRandom(1817);

    // 2b) "Krøllet folie": bløde, svage lyse/mørke pletter (radiale gradienter), slørede hvor browseren kan
    c.save();
    if ('filter' in c) /** @type {any} */ (c).filter = 'blur(5px)';
    for (let i = 0; i < 22; i += 1) {
      const px = rand() * w;
      const py = rand() * h;
      const r = 18 + rand() * Math.min(w, h) * 0.3;
      const light = rand() > 0.45;
      const g = c.createRadialGradient(px, py, 0, px, py, r);
      g.addColorStop(0, light ? 'rgba(255, 246, 205, 0.16)' : 'rgba(120, 88, 24, 0.14)');
      g.addColorStop(1, light ? 'rgba(255, 246, 205, 0)' : 'rgba(120, 88, 24, 0)');
      c.fillStyle = g;
      c.fillRect(px - r, py - r, r * 2, r * 2);
    }
    c.restore();

    // 3) Folie-struktur: fint korn (mønster) + få, meget svage børstede hårstreger
    const tile = getNoiseTile();
    if (tile) {
      const pattern = c.createPattern(tile, 'repeat');
      if (pattern) {
        // Kornet i fysiske pixels (ikke CSS-pixels) → fin folie i stedet for sandpapir på retina
        if (typeof pattern.setTransform === 'function' && typeof DOMMatrix === 'function') {
          pattern.setTransform(new DOMMatrix().scale(1 / dpr));
        }
        c.fillStyle = pattern;
        c.fillRect(0, 0, w, h);
      }
    }
    c.lineCap = 'round';
    c.lineWidth = 0.6;
    for (let i = 0; i < 26; i += 1) {
      const y = rand() * h;
      const x = rand() * w * 0.6;
      c.strokeStyle = rand() > 0.5 ? 'rgba(255, 250, 230, 0.16)' : 'rgba(110, 80, 20, 0.1)';
      c.beginPath();
      c.moveTo(x, y);
      c.lineTo(x + w * (0.25 + rand() * 0.5), y - h * 0.08);
      c.stroke();
    }

    // 4) Skrabemærker som på et rigtigt skrabelod (Alias kort): 5 skrå, næsten lige "mønt-strøg" i en vifte om
    //    midten. Hvert strøg: jævn bredde med afrundede, let tilspidsede ender, flosset kant, fyldt i mørk
    //    halvgennemsigtig brun (multiply) + tynde lyse/mørke streger på langs = den "skrabede" struktur.
    const marks = 5;
    for (let i = 0; i < marks; i += 1) {
      const deg = 74 + rand() * 9 + (i - 2) * 1.2;
      const rad = (deg * Math.PI) / 180;
      const length = h * (0.62 + rand() * 0.16);
      const width = 20 + rand() * 6;
      const cx = w / 2 + (i - (marks - 1) / 2) * (w * 0.086) + (rand() - 0.5) * 8;
      const cy = h / 2 + (rand() - 0.5) * h * 0.12;
      const ux = Math.cos(rad);
      const uy = -Math.sin(rad);
      const nx = -uy; // normal på strøget
      const ny = ux;
      const x1 = cx - (ux * length) / 2;
      const y1 = cy - (uy * length) / 2;
      const endA = 0.1 + rand() * 0.06; // hvor langt ind enderne er tilspidsede
      const endB = 0.1 + rand() * 0.06;
      const steps = 28;

      /** Halv bredde langs strøget: fuld på midten, ender rundet ned til ~30 % */
      const halfAt = (t) => {
        let k = 1;
        if (t < endA) k = 0.3 + 0.7 * Math.sin((Math.PI / 2) * (t / endA));
        else if (t > 1 - endB) k = 0.3 + 0.7 * Math.sin((Math.PI / 2) * ((1 - t) / endB));
        return (width / 2) * k;
      };
      /** Kantpunkt med flosset kant (små hak, af og til et større) */
      const edge = (t, side) => {
        const rough = (rand() - 0.5) * 2.4 + (rand() > 0.85 ? (rand() - 0.5) * 5 : 0);
        const off = side * (halfAt(t) + rough * 0.5);
        return { x: x1 + ux * length * t + nx * off, y: y1 + uy * length * t + ny * off };
      };

      c.save();
      c.globalCompositeOperation = 'multiply';
      c.beginPath();
      for (let j = 0; j <= steps; j += 1) {
        const p = edge(j / steps, 1);
        if (j === 0) c.moveTo(p.x, p.y);
        else c.lineTo(p.x, p.y);
      }
      for (let j = steps; j >= 0; j -= 1) {
        const p = edge(j / steps, -1);
        c.lineTo(p.x, p.y);
      }
      c.closePath();
      c.fillStyle = `rgba(76, 57, 22, ${(0.27 + rand() * 0.07).toFixed(3)})`;
      c.fill();
      // Mørkere kerne (lidt smallere) → dybde
      c.beginPath();
      for (let j = 0; j <= steps; j += 1) {
        const t = j / steps;
        const off = halfAt(t) * 0.5;
        const px = x1 + ux * length * t + nx * off;
        const py = y1 + uy * length * t + ny * off;
        if (j === 0) c.moveTo(px, py);
        else c.lineTo(px, py);
      }
      for (let j = steps; j >= 0; j -= 1) {
        const t = j / steps;
        const off = -halfAt(t) * 0.5;
        c.lineTo(x1 + ux * length * t + nx * off, y1 + uy * length * t + ny * off);
      }
      c.closePath();
      c.fillStyle = 'rgba(60, 44, 14, 0.12)';
      c.fill();
      c.restore();

      // Skrabe-struktur: tynde streger på langs inde i strøget – mørke (multiply) og lyse (ovenpå)
      c.save();
      c.lineCap = 'round';
      for (let k = 0; k < 9; k += 1) {
        const offN = (rand() - 0.5) * width * 0.78;
        const from = rand() * 0.2;
        const to = 0.75 + rand() * 0.25;
        const light = rand() > 0.6;
        c.globalCompositeOperation = light ? 'source-over' : 'multiply';
        c.strokeStyle = light
          ? `rgba(255, 243, 205, ${(0.14 + rand() * 0.14).toFixed(3)})`
          : `rgba(52, 38, 12, ${(0.14 + rand() * 0.16).toFixed(3)})`;
        c.lineWidth = light ? 0.6 + rand() * 0.9 : 0.8 + rand() * 1.6;
        c.beginPath();
        c.moveTo(x1 + ux * length * from + nx * offN, y1 + uy * length * from + ny * offN);
        c.lineTo(x1 + ux * length * to + nx * (offN + (rand() - 0.5) * 2), y1 + uy * length * to + ny * (offN + (rand() - 0.5) * 2));
        c.stroke();
      }
      c.restore();
    }
    } // slut: temaets egen folie

    // 5) Tekst over ridserne: versaler, Helvetica Neue 400, ca. 0,14em luft. Mørk "præget" tekst (mørkebrun +
    //    lys kant nedenunder): hvid tekst på guld gav kun ca. 1,5:1 i kontrast (iOS-test 17/10 2026).
    const family = getComputedStyle(dialog).fontFamily || '"Helvetica Neue", Helvetica, Arial, sans-serif';
    const size = desktop.matches ? 22 : 20;
    c.font = `400 ${size}px ${family}`;
    c.textBaseline = 'middle';
    c.fillStyle = 'rgba(38, 27, 6, 0.86)';
    c.shadowColor = 'rgba(255, 246, 214, 0.6)';
    c.shadowBlur = 0;
    c.shadowOffsetY = 1;
    fillSpacedText(c, String(config.coverText || '').toUpperCase(), w / 2, h / 2, size * 0.14);

    // Nulstil skygge – ellers får skrabe-penslen (destination-out) også skygge
    c.shadowColor = 'transparent';
    c.shadowBlur = 0;
    c.shadowOffsetY = 0;

    // Skrab: destination-out fjerner præcis så meget, som penslens alpha – derfor helt opak farve
    c.globalCompositeOperation = 'destination-out';
    c.strokeStyle = '#000';
    c.fillStyle = '#000';
    c.lineWidth = BRUSH * 2;
    c.lineCap = 'round';
    c.lineJoin = 'round';
  }

  /** @param {PointerEvent} e */
  function pointFrom(e) {
    const rect = canvas.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  }

  /** @param {{ x: number, y: number }} p */
  function scratchTo(p) {
    if (!ctx) return;
    ctx.beginPath();
    if (last) {
      ctx.moveTo(last.x, last.y);
      ctx.lineTo(p.x, p.y);
      ctx.stroke();
    } else {
      ctx.arc(p.x, p.y, BRUSH, 0, Math.PI * 2);
      ctx.fill();
    }
    last = p;
  }

  function checkCoverage() {
    if (!ctx || revealed) return;
    try {
      const amount = logic.coverage(ctx.getImageData(0, 0, canvas.width, canvas.height));
      if (amount >= REVEAL_AT) reveal();
    } catch (e) {
      reveal(); // fx sikkerhedsfejl i getImageData – vis gevinsten frem for at hænge
    }
  }

  /** @param {PointerEvent} e */
  function onPointerDown(e) {
    if (revealed) return;
    if (reducedMotion.matches) {
      reveal();
      return;
    }
    e.preventDefault();
    try {
      canvas.setPointerCapture(e.pointerId);
    } catch (err) { /* ignorer */ }
    card.classList.add('is-scratching', 'is-drawing'); // stopper shine-overlayet, cursor → grabbing
    drawing = true;
    last = null;
    moves = 0;
    scratchTo(pointFrom(e));
  }

  /** @param {PointerEvent} e */
  function onPointerMove(e) {
    if (revealed || !drawing) return;
    scratchTo(pointFrom(e));
    moves += 1;
    if (moves % 10 === 0) checkCoverage();
  }

  /** @param {PointerEvent} e */
  function onPointerUp(e) {
    if (revealed || !drawing) return;
    drawing = false;
    card.classList.remove('is-drawing');
    try {
      if (canvas.hasPointerCapture(e.pointerId)) canvas.releasePointerCapture(e.pointerId);
    } catch (err) { /* ignorer */ }
    last = null;
    checkCoverage();
  }

  /**
   * Afsløret: kortet fader ud og klapper sammen (opacity 0 + scale .96, 350 ms), gevinsten står på dets plads
   * (samme grid-celle – styres af .is-revealed i CSS). instant = genåbning via flydende knap / reduceret bevægelse.
   * @param {boolean} [instant]
   */
  function setRevealedUi(instant) {
    const scratchStep = /** @type {HTMLElement} */ (dialog.querySelector('[data-csp-step="scratch"]'));
    const now = Boolean(instant || reducedMotion.matches);
    scratchStep.classList.toggle('is-instant', now);
    scratchStep.classList.add('is-revealed');
    card.classList.remove('is-drawing');
    card.removeAttribute('role');
    card.removeAttribute('tabindex');
    card.removeAttribute('aria-label');
    card.setAttribute('aria-hidden', 'true');
    if (now) {
      canvas.hidden = true;
    } else {
      window.setTimeout(() => { canvas.hidden = true; }, 350);
    }
  }

  function reveal() {
    if (revealed) return;
    revealed = true;
    const hadFocus = dialog.contains(document.activeElement) && document.activeElement === card;
    setRevealedUi(false);
    live.textContent = config.winText || '';
    track('custom_popup_scratch');
    const claim = /** @type {HTMLElement | null} */ (dialog.querySelector('[data-csp-claim]'));
    // Fokus til HENT, så tastatur/skærmlæser kan fortsætte (på touch kun hvis kortet havde fokus)
    if (hadFocus || !window.matchMedia('(pointer: coarse)').matches) claim?.focus({ preventScroll: true });
  }

  /* ---------- trin ---------- */

  /**
   * @param {'scratch' | 'email' | 'success'} next
   * @param {boolean} [focus]
   */
  function goTo(next, focus = true) {
    step = next;
    dialog.querySelectorAll('[data-csp-step]').forEach((el) => {
      /** @type {HTMLElement} */ (el).hidden = el.getAttribute('data-csp-step') !== next;
    });
    dialog.setAttribute('data-step', next);
    const content = dialog.querySelector('.custom-scratch-popup__content');
    if (content) content.scrollTop = 0;
    dialog.scrollTop = 0;
    if (!focus) return;
    // Fokus på trinnets overskrift (tabindex=-1) – ikke på første svar: programmatisk fokus på radioen fik
    // browseren til at vise fokusrammen om "TIL MIG SELV", så den lignede valgt (fundet af ejer 17/10).
    const heading = /** @type {HTMLElement | null} */ (dialog.querySelector(`[data-csp-step="${next}"] h2`));
    heading?.focus({ preventScroll: true });
  }

  /* ---------- scroll-lås ---------- */

  /* overflow:hidden alene holder ikke på iOS Safari: fokus i e-mailfeltet scrollede siden bagved
     (110–450 px, fundet i iOS-test 17/10 2026). Derfor fastlåses body på sin position og gendannes ved luk. */
  let lockedScrollY = 0;

  function lockScroll() {
    lockedScrollY = window.scrollY;
    const body = document.body;
    body.style.position = 'fixed';
    body.style.top = `-${lockedScrollY}px`;
    body.style.left = '0';
    body.style.right = '0';
    body.style.width = '100%';
    document.documentElement.classList.add('custom-scratch-popup-open');
  }

  function unlockScroll() {
    const body = document.body;
    body.style.position = '';
    body.style.top = '';
    body.style.left = '';
    body.style.right = '';
    body.style.width = '';
    document.documentElement.classList.remove('custom-scratch-popup-open');
    // 'instant': ellers glider en evt. scroll-behavior: smooth hele vejen tilbage
    window.scrollTo({ top: lockedScrollY, left: 0, behavior: /** @type {ScrollBehavior} */ ('instant') });
  }

  /* ---------- åbn / luk ---------- */

  /** @param {string} trigger */
  function open(trigger) {
    if (dialog.open) return;
    shown = true;
    hideFab();
    clearErrors(); // fx "Indtast en gyldig e-mailadresse" fra sidst – svar og e-mail bevares
    lockScroll();
    dialog.showModal();
    goTo(step, false);
    // Canvas kan først måles, når dialogen er synlig
    window.requestAnimationFrame(() => {
      drawCover();
      // Fokus på overskriften (ikke luk-krydset, som showModal ellers vælger); Tab → skrabekortet
      if (step === 'scratch' && revealed) /** @type {HTMLElement | null} */ (dialog.querySelector('[data-csp-claim]'))?.focus({ preventScroll: true });
      else goTo(step, true);
    });
    track('custom_popup_view', { trigger, step });
  }

  /** Lukket af brugeren (X, Esc, baggrund) – eller "Tilbage til shoppen" efter tilmelding */
  function close() {
    if (!dialog.open) return;
    dialog.close();
    unlockScroll();
    if (subscribed || step === 'success') return;
    track('custom_popup_close', { step });
    saveState({ dismissedAt: Date.now() });
    if (!fabDismissed) {
      saveSessionFab();
      showFab(true);
    }
  }

  /** @param {boolean} [focus] */
  function showFab(focus) {
    if (subscribed || fabDismissed) return;
    fab.hidden = false;
    if (focus) {
      // Efter sidens øvrige Esc-/klik-lyttere – ellers ender fokus på <body>
      setTimeout(() => /** @type {HTMLElement | null} */ (fab.querySelector('[data-csp-fab-open]'))?.focus({ preventScroll: true }), 0);
    }
  }

  function hideFab() {
    fab.hidden = true;
  }

  function dismissFab() {
    fabDismissed = true;
    hideFab();
    clearSessionFab();
    saveState({ dismissedAt: Date.now() });
    track('custom_popup_fab_dismiss');
  }

  /* ---------- tilmelding ---------- */

  /** E-mail-/afsendelsesfejl – under e-mailfeltet @param {string} message */
  function showError(message) {
    errorEl.textContent = message;
  }

  /** Fejl for svaret – lige under valgene, som får en fejlramme @param {string} message */
  function showAnswerError(message) {
    answerErrorEl.textContent = message;
    chipsEl.classList.toggle('is-invalid', Boolean(message));
  }

  function clearErrors() {
    showError('');
    showAnswerError('');
    emailInput.removeAttribute('aria-invalid');
  }

  /** @param {string} email @param {string} answer */
  async function subscribe(email, answer) {
    const body = {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: 'Skrab-popup',
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email,
                properties: { 'Skrab-popup svar': answer, 'Tilmeldt via': 'Skrab-popup' },
                subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } },
              },
            },
          },
        },
        relationships: { list: { data: { type: 'list', id: config.listId } } },
      },
    };
    const controller = typeof AbortController === 'function' ? new AbortController() : null;
    const timer = controller ? window.setTimeout(() => controller.abort(), 15000) : 0;
    try {
      const res = await fetch(KLAVIYO_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', revision: '2025-04-15' },
        body: JSON.stringify(body),
        signal: controller ? controller.signal : undefined,
      });
      return res.status === 202 || res.ok;
    } finally {
      window.clearTimeout(timer);
    }
  }

  /** @param {string} email */
  function identify(email) {
    try {
      if (win.klaviyo && typeof win.klaviyo.push === 'function') {
        win.klaviyo.push(['identify', { email }]);
      } else if (win._learnq && typeof win._learnq.push === 'function') {
        win._learnq.push(['identify', { $email: email }]);
      }
    } catch (e) { /* ignorer */ }
  }

  /** @param {SubmitEvent} e */
  async function onSubmit(e) {
    e.preventDefault();
    if (submitBtn.disabled) return;
    const checked = /** @type {HTMLInputElement | null} */ (form.querySelector('input[type="radio"]:checked'));
    const email = emailInput.value.trim();
    // Begge fejl vises på én gang (hver ved sit felt), fokus på det første felt med fejl
    const emailOk = logic.isValidEmail(email);
    showAnswerError(checked ? '' : config.texts.errorAnswer);
    showError(emailOk ? '' : config.texts.errorEmail);
    if (emailOk) emailInput.removeAttribute('aria-invalid');
    else emailInput.setAttribute('aria-invalid', 'true');
    if (!checked) {
      /** @type {HTMLElement | null} */ (form.querySelector('input[type="radio"]'))?.focus({ preventScroll: true });
      return;
    }
    if (!emailOk) {
      emailInput.focus();
      return;
    }
    submitBtn.disabled = true;
    submitBtn.setAttribute('aria-busy', 'true');
    submitBtn.textContent = config.texts.sending;

    let ok = false;
    try {
      ok = await subscribe(email, checked.value);
    } catch (err) {
      ok = false;
    }

    submitBtn.disabled = false;
    submitBtn.removeAttribute('aria-busy');
    submitBtn.textContent = submitLabel;

    if (!ok) {
      showError(config.texts.errorGeneric);
      return;
    }
    subscribed = true;
    saveState({ subscribed: true });
    clearSessionFab();
    hideFab();
    identify(email);
    track('custom_popup_signup', { answer: checked.value });
    goTo('success');
  }

  /* ---------- trigger: samtykke → tid / scroll ---------- */

  function isBusy() {
    const html = document.documentElement;
    return (
      html.classList.contains('js-drawer-open') ||
      html.classList.contains('modal-open') ||
      document.body.classList.contains('modal-open') ||
      Boolean(document.querySelector('[data-gw-overlay].is-open')) ||
      Array.from(document.querySelectorAll('dialog[open]')).some((d) => d !== dialog)
    );
  }

  /** @param {string} trigger */
  function fire(trigger) {
    if (triggered) return;
    triggered = true;
    window.removeEventListener('scroll', onScroll);
    const attempt = () => {
      if (isBusy()) {
        window.setTimeout(attempt, 1000); // vent til kurv-skuffe/andre popups er lukket
        return;
      }
      open(trigger);
    };
    attempt();
  }

  function scrollPercent() {
    const max = document.documentElement.scrollHeight - window.innerHeight;
    return max > 0 ? (window.scrollY / max) * 100 : 0;
  }

  function onScroll() {
    if (scrollPercent() >= config.scroll) fire('scroll');
  }

  function startTriggers() {
    window.setTimeout(() => fire('timer'), Math.max(0, Number(config.delay) || 10) * 1000);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  /** Starter callback, når kunden har svaret på cookie-banneret (eller det ikke skal vises). Fallback efter 1,5 s uden API/Pandectes. */
  function afterConsent(/** @type {() => void} */ callback) {
    let done = false;
    const go = () => {
      if (done) return;
      done = true;
      callback();
    };

    // Pandectes: banneret er <pandectes-cmp> med indholdet i en shadow root (ingen id/klasse med "pandectes"), og
    // cookien _pandectes_gdpr findes først, når kunden har valgt. Shopify-API'et alene var ikke nok: popuppen
    // kom oven på et ubesvaret banner på en helt ny iPhone (iOS-test 17/10 2026).
    const pandectesInstalled = () => Boolean(win.pandectesBannerSettings || win.PandectesCore || document.querySelector('pandectes-cmp'));

    const consentCollected = () => {
      if (pandectesInstalled()) return Boolean(readCookie('_pandectes_gdpr'));
      try {
        const consent = win.Shopify.customerPrivacy.currentVisitorConsent?.();
        // sale_of_data står som standard på "yes" i EU (ikke et valg) → tæller ikke med
        return Boolean(consent && Object.entries(consent).some(([k, v]) => k !== 'sale_of_data' && (v === 'yes' || v === 'no')));
      } catch (e) {
        return false;
      }
    };

    /** Er et cookie-banner synligt? <pandectes-cmp> er fixed (offsetParent er altid null) – mål indholdet i shadow root */
    const bannerVisible = () => {
      const isShown = (/** @type {Element} */ el) => {
        const r = el.getBoundingClientRect();
        return r.width > 0 && r.height > 0 && getComputedStyle(el).visibility !== 'hidden';
      };
      const host = document.querySelector('pandectes-cmp');
      if (host) {
        const root = host.shadowRoot;
        return root ? Array.from(root.querySelectorAll('*')).some(isShown) : isShown(host);
      }
      const other = document.querySelector('[id*="pandectes"], [class*="pandectes"], [id*="cookie-banner"], [class*="cookie-banner"], [id*="shopify-pc__banner"]');
      return Boolean(other && isShown(other));
    };

    let waiting = false;
    /** Banneret skal vises: vent på valget (event + polling). Er intet banner synligt efter 8 s
        (fx slået fra for regionen/preview), startes alligevel – ellers kom popuppen aldrig. */
    const waitForChoice = () => {
      if (waiting) return;
      waiting = true;
      document.addEventListener('visitorConsentCollected', () => go(), { once: true });
      const poll = window.setInterval(() => {
        if (done) window.clearInterval(poll);
        else if (consentCollected()) {
          window.clearInterval(poll);
          go();
        }
      }, 1000);
      window.setTimeout(() => {
        if (done || bannerVisible()) return;
        window.clearInterval(poll);
        go();
      }, 8000);
    };

    const check = () => {
      if (consentCollected()) {
        go();
        return true;
      }
      if (pandectesInstalled()) {
        waitForChoice();
        return true;
      }
      const privacy = win.Shopify && win.Shopify.customerPrivacy;
      if (!privacy || typeof privacy.shouldShowBanner !== 'function') return false;
      let showBanner = false;
      try {
        showBanner = Boolean(privacy.shouldShowBanner());
      } catch (e) {
        showBanner = false;
      }
      if (!showBanner) go();
      else waitForChoice();
      return true;
    };

    // Pandectes er (som regel) defineret, før dette script kører → afgør med det samme
    if (check()) return;

    let handled = false;
    const fallback = window.setTimeout(() => {
      if (!handled) go(); // API'et kom ikke inden 1,5 s → start alligevel
    }, 1500);

    try {
      if (win.Shopify && typeof win.Shopify.loadFeatures === 'function') {
        win.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (/** @type {unknown} */ error) => {
          if (error || done) return;
          handled = check();
          if (handled) window.clearTimeout(fallback);
        });
      }
    } catch (e) { /* fallback-timeren klarer det */ }
  }

  /* ---------- lyttere ---------- */

  card.addEventListener('keydown', (e) => {
    if (revealed) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      reveal();
    }
  });
  // Klik uden mus/touch (skærmlæser, e.detail === 0) afslører direkte
  card.addEventListener('click', (e) => {
    if (!revealed && e.detail === 0) reveal();
  });
  canvas.addEventListener('pointerdown', onPointerDown);
  canvas.addEventListener('pointermove', onPointerMove);
  canvas.addEventListener('pointerup', onPointerUp);
  canvas.addEventListener('pointercancel', onPointerUp);
  // Kortets størrelse skifter ved 1024 px (og CSS'en indlæses asynkront) → tegn fladen igen i den rigtige størrelse
  if (typeof ResizeObserver === 'function') {
    new ResizeObserver(() => {
      if (dialog.open) drawCover();
    }).observe(card);
  }

  dialog.querySelector('[data-csp-claim]')?.addEventListener('click', () => {
    goTo('email');
    track('custom_popup_step', { step: 'email' });
  });
  form.addEventListener('submit', /** @type {EventListener} */ (/** @type {unknown} */ (onSubmit)));
  form.addEventListener('change', (e) => {
    if (/** @type {HTMLInputElement} */ (e.target).type === 'radio') showAnswerError('');
  });
  // E-mail-fejlen forsvinder, så snart adressen er gyldig (ikke ved hvert tastetryk før)
  emailInput.addEventListener('input', () => {
    if (errorEl.textContent === config.texts.errorEmail && logic.isValidEmail(emailInput.value)) {
      showError('');
      emailInput.removeAttribute('aria-invalid');
    }
  });
  dialog.querySelector('[data-csp-close]')?.addEventListener('click', close);
  dialog.querySelector('[data-csp-done]')?.addEventListener('click', close);

  // Klik på baggrunden lukker. stopPropagation: Motion lukker skuffer ved ethvert klik på window.
  // Kun hvis trykket OGSÅ startede på baggrunden: et skrab, der slipper uden for panelet, giver ellers et
  // click med dialogen som fælles forælder – og lukkede popuppen midt i skrabningen (fundet i test 17/10 2026).
  let pressedOnBackdrop = false;
  dialog.addEventListener('pointerdown', (e) => {
    pressedOnBackdrop = e.target === dialog;
  });
  dialog.addEventListener('click', (e) => {
    e.stopPropagation();
    if (e.target !== dialog) return;
    if (!pressedOnBackdrop) return;
    const r = dialog.getBoundingClientRect();
    const outside = e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom;
    if (outside) close();
  });

  // Esc: vores egen luk-logik (FAB osv.). Som i custom-terms-modal: også på keydown, fordi andre scripts
  // kan stoppe Esc, så 'cancel' aldrig kommer – og keyup sluges, så Motions drawer-lytter ikke reagerer.
  let swallowEscUp = false;
  const onEsc = (/** @type {Event} */ e) => {
    e.preventDefault();
    swallowEscUp = true;
    close();
  };
  dialog.addEventListener('cancel', onEsc);
  dialog.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    e.stopPropagation();
    onEsc(e);
  });
  window.addEventListener('keyup', (e) => {
    if (!swallowEscUp || e.key !== 'Escape') return;
    swallowEscUp = false;
    e.stopImmediatePropagation();
  }, true);

  fab.querySelector('[data-csp-fab-open]')?.addEventListener('click', () => open('fab'));
  fab.querySelector('[data-csp-fab-dismiss]')?.addEventListener('click', dismissFab);

  // Vist men ikke tilmeldt → næste sideindlæsning viser ikke popuppen igen før "days" er gået,
  // men FAB'en kommer igen i samme session (medmindre den er krydset væk).
  const onLeave = () => {
    if (!shown || subscribed || designMode) return;
    saveState({ dismissedAt: Date.now() });
    if (!fabDismissed && step !== 'success') saveSessionFab();
  };
  window.addEventListener('pagehide', onLeave);
  window.addEventListener('beforeunload', onLeave);

  /* ---------- start ---------- */

  if (designMode) {
    // Temaeditoren: vis med det samme, ingen spærrer (efter 'load', så den asynkrone CSS er på plads)
    if (document.readyState === 'complete') open('design-mode');
    else window.addEventListener('load', () => open('design-mode'), { once: true });
    return;
  }

  if (testMode) {
    // Testtilstand: ingen spærrer (tilmeldt/lukket/Klaviyo/utm), men normal timer/scroll efter samtykke
    afterConsent(startTriggers);
    return;
  }

  const state = readState();
  if (state.subscribed) return;

  // Samme session, popuppen er lukket uden tilmelding → kun FAB'en (på det trin man nåede)
  const sessionFab = readSessionFab();
  if (sessionFab) {
    shown = true;
    if (sessionFab.revealed) {
      revealed = true;
      setRevealedUi(true);
    }
    step = sessionFab.step === 'email' && revealed ? 'email' : 'scratch';
    showFab(false);
    return;
  }

  const decision = logic.shouldShow({
    state,
    now: Date.now(),
    days: Number(config.days) || 14,
    klaviyoIdentified: logic.isKlaviyoIdentified(readCookie('__kla_id')),
    utm: logic.hasMarketingUtm(window.location.search),
    designMode,
  });
  if (!decision.show) return;

  afterConsent(startTriggers);
})();
